// Comprehensive Talent Rating System
// Provides accurate, data-driven ratings for athletes across all sports

import { getSportConfig, SportStatConfig } from './sportConfigs';

// ============ INTERFACES ============

export interface TalentRatingInput {
  // Athletic Performance Data
  sport: string;
  position?: string;
  league?: string;
  teamName?: string;
  careerStats?: Record<string, number>;
  currentSeasonStats?: Record<string, number>;
  
  // Social Influence Data
  followers?: number;
  engagementRate?: number;
  platformCount?: number;
  
  // Business Value Data
  completedDeals?: number;
  totalRevenue?: number;
  marketValue?: number;
  partnershipCount?: number;
  
  // Profile Quality Data
  isVerified?: boolean;
  isClaimed?: boolean;
  profileCompleteness?: number;
  lastActiveDate?: Date;
  hasPhoto?: boolean;
}

export interface RatingBreakdown {
  athletic: number;
  social: number;
  business: number;
  profile: number;
}

export interface TalentRating {
  overall: number;
  breakdown: RatingBreakdown;
  tier: TalentTier;
  tierColor: string;
  percentile: number;
  grade: string;
  confidence: number;
}

export type TalentTier = 'Bronze' | 'Silver' | 'Gold' | 'Platinum' | 'Elite';

// ============ CONSTANTS ============

export const CATEGORY_WEIGHTS = {
  athletic: 0.35,
  social: 0.25,
  business: 0.25,
  profile: 0.15,
};

export const TIER_THRESHOLDS: Record<TalentTier, { min: number; color: string }> = {
  Elite: { min: 90, color: 'from-violet-500 to-purple-600' },
  Platinum: { min: 80, color: 'from-slate-300 to-slate-500' },
  Gold: { min: 65, color: 'from-yellow-400 to-amber-500' },
  Silver: { min: 50, color: 'from-gray-300 to-gray-400' },
  Bronze: { min: 0, color: 'from-orange-600 to-orange-800' },
};

export const GRADE_THRESHOLDS = [
  { min: 95, grade: 'S+' },
  { min: 90, grade: 'S' },
  { min: 85, grade: 'A+' },
  { min: 80, grade: 'A' },
  { min: 75, grade: 'B+' },
  { min: 70, grade: 'B' },
  { min: 65, grade: 'C+' },
  { min: 60, grade: 'C' },
  { min: 55, grade: 'D+' },
  { min: 50, grade: 'D' },
  { min: 0, grade: 'F' },
];

// ============ HELPER FUNCTIONS ============

/**
 * Applies logarithmic scaling for values with extreme ranges (like follower counts)
 */
const logarithmicScale = (value: number, maxValue: number): number => {
  if (value <= 0) return 0;
  const logValue = Math.log10(value + 1);
  const logMax = Math.log10(maxValue + 1);
  return Math.min(1, logValue / logMax);
};

/**
 * Applies linear scaling with capping
 */
const linearScale = (value: number, maxValue: number, higherIsBetter: boolean = true): number => {
  if (maxValue === 0) return 0;
  const normalized = Math.min(1, Math.max(0, value / maxValue));
  return higherIsBetter ? normalized : 1 - normalized;
};

/**
 * Calculates the completeness of data for confidence scoring
 */
const calculateDataConfidence = (input: TalentRatingInput): number => {
  let totalFields = 0;
  let filledFields = 0;

  // Athletic fields
  const athleticFields = ['careerStats', 'currentSeasonStats', 'position', 'league', 'teamName'];
  athleticFields.forEach(field => {
    totalFields++;
    if (input[field as keyof TalentRatingInput]) filledFields++;
  });

  // Social fields
  const socialFields = ['followers', 'engagementRate', 'platformCount'];
  socialFields.forEach(field => {
    totalFields++;
    if (input[field as keyof TalentRatingInput] !== undefined) filledFields++;
  });

  // Business fields
  const businessFields = ['completedDeals', 'totalRevenue', 'marketValue', 'partnershipCount'];
  businessFields.forEach(field => {
    totalFields++;
    if (input[field as keyof TalentRatingInput] !== undefined) filledFields++;
  });

  // Profile fields
  const profileFields = ['isVerified', 'isClaimed', 'profileCompleteness', 'hasPhoto'];
  profileFields.forEach(field => {
    totalFields++;
    if (input[field as keyof TalentRatingInput] !== undefined) filledFields++;
  });

  return Math.round((filledFields / totalFields) * 100);
};

// ============ CATEGORY CALCULATORS ============

/**
 * Calculates athletic performance score (0-100)
 */
export const calculateAthleticScore = (input: TalentRatingInput): number => {
  const config = getSportConfig(input.sport);
  let totalScore = 0;
  let totalWeight = 0;

  // Process primary stats
  config.primaryStats.forEach(stat => {
    const value = input.currentSeasonStats?.[stat.key] ?? input.careerStats?.[stat.key] ?? 0;
    const normalized = linearScale(value, stat.maxValue, stat.higherIsBetter);
    totalScore += normalized * stat.weight * 100;
    totalWeight += stat.weight;
  });

  // Process secondary stats
  config.secondaryStats.forEach(stat => {
    const value = input.currentSeasonStats?.[stat.key] ?? input.careerStats?.[stat.key] ?? 0;
    const normalized = linearScale(value, stat.maxValue, stat.higherIsBetter);
    totalScore += normalized * stat.weight * 100;
    totalWeight += stat.weight;
  });

  // Apply position multiplier
  const positionMultiplier = input.position 
    ? config.positionMultipliers[input.position] ?? 1.0
    : 1.0;

  // Apply league prestige multiplier
  const leagueMultiplier = input.league
    ? config.leaguePrestige[input.league] ?? config.leaguePrestige['default'] ?? 0.7
    : config.leaguePrestige['default'] ?? 0.7;

  // Calculate base score
  let baseScore = totalWeight > 0 ? (totalScore / totalWeight) : 30; // Default to 30 if no stats

  // Apply multipliers
  baseScore = baseScore * positionMultiplier * leagueMultiplier;

  // Add bonus for having a team
  if (input.teamName) {
    baseScore += 5;
  }

  return Math.min(100, Math.max(0, Math.round(baseScore)));
};

/**
 * Calculates social influence score (0-100)
 */
export const calculateSocialScore = (input: TalentRatingInput): number => {
  let score = 0;

  // Follower count (logarithmic scale, max 50M for top score)
  if (input.followers !== undefined) {
    const followerScore = logarithmicScale(input.followers, 50000000) * 50;
    score += followerScore;
  }

  // Engagement rate (linear scale, 10% is excellent)
  if (input.engagementRate !== undefined) {
    const engagementScore = linearScale(input.engagementRate, 10) * 30;
    score += engagementScore;
  } else {
    // Default engagement assumption based on follower count
    const assumedEngagement = input.followers 
      ? Math.max(1, 8 - Math.log10(input.followers + 1))
      : 2;
    score += linearScale(assumedEngagement, 10) * 15;
  }

  // Platform diversity (having presence on multiple platforms)
  if (input.platformCount !== undefined) {
    const platformScore = linearScale(input.platformCount, 5) * 20;
    score += platformScore;
  } else {
    score += 5; // Assume at least one platform
  }

  return Math.min(100, Math.max(0, Math.round(score)));
};

/**
 * Calculates business value score (0-100)
 */
export const calculateBusinessScore = (input: TalentRatingInput): number => {
  let score = 0;

  // Deal completion history (max 50 deals for full score)
  if (input.completedDeals !== undefined) {
    const dealScore = linearScale(input.completedDeals, 50) * 30;
    score += dealScore;
  }

  // Total revenue generated (logarithmic, max $10M for full score)
  if (input.totalRevenue !== undefined) {
    const revenueScore = logarithmicScale(input.totalRevenue, 10000000) * 30;
    score += revenueScore;
  }

  // Market value estimate (logarithmic, max $100M for full score)
  if (input.marketValue !== undefined) {
    const marketScore = logarithmicScale(input.marketValue, 100000000) * 25;
    score += marketScore;
  } else if (input.followers !== undefined) {
    // Estimate market value based on followers ($0.10-$1 per follower depending on engagement)
    const estimatedValue = input.followers * (input.engagementRate ? input.engagementRate / 10 : 0.3);
    const marketScore = logarithmicScale(estimatedValue, 100000000) * 15;
    score += marketScore;
  }

  // Partnership diversity (multiple brand partnerships)
  if (input.partnershipCount !== undefined) {
    const partnerScore = linearScale(input.partnershipCount, 20) * 15;
    score += partnerScore;
  }

  return Math.min(100, Math.max(0, Math.round(score)));
};

/**
 * Calculates profile quality score (0-100)
 */
export const calculateProfileScore = (input: TalentRatingInput): number => {
  let score = 0;

  // Verified status (major boost)
  if (input.isVerified) {
    score += 35;
  }

  // Claimed profile status
  if (input.isClaimed) {
    score += 25;
  }

  // Profile completeness
  if (input.profileCompleteness !== undefined) {
    score += linearScale(input.profileCompleteness, 100) * 25;
  } else {
    // Estimate based on available data
    let completeness = 0;
    if (input.hasPhoto) completeness += 20;
    if (input.teamName) completeness += 15;
    if (input.position) completeness += 15;
    if (input.careerStats) completeness += 25;
    if (input.followers) completeness += 25;
    score += linearScale(completeness, 100) * 25;
  }

  // Photo availability
  if (input.hasPhoto) {
    score += 10;
  }

  // Recent activity bonus
  if (input.lastActiveDate) {
    const daysSinceActive = Math.floor(
      (Date.now() - input.lastActiveDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceActive < 7) {
      score += 5;
    } else if (daysSinceActive < 30) {
      score += 3;
    }
  }

  return Math.min(100, Math.max(0, Math.round(score)));
};

// ============ MAIN RATING CALCULATOR ============

/**
 * Calculates the comprehensive talent rating
 */
export const calculateTalentRating = (input: TalentRatingInput): TalentRating => {
  // Calculate individual category scores
  const breakdown: RatingBreakdown = {
    athletic: calculateAthleticScore(input),
    social: calculateSocialScore(input),
    business: calculateBusinessScore(input),
    profile: calculateProfileScore(input),
  };

  // Calculate weighted overall score
  const overall = Math.round(
    breakdown.athletic * CATEGORY_WEIGHTS.athletic +
    breakdown.social * CATEGORY_WEIGHTS.social +
    breakdown.business * CATEGORY_WEIGHTS.business +
    breakdown.profile * CATEGORY_WEIGHTS.profile
  );

  // Determine tier
  let tier: TalentTier = 'Bronze';
  let tierColor = TIER_THRESHOLDS.Bronze.color;
  for (const [tierName, threshold] of Object.entries(TIER_THRESHOLDS)) {
    if (overall >= threshold.min) {
      tier = tierName as TalentTier;
      tierColor = threshold.color;
      break;
    }
  }

  // Determine grade
  const grade = GRADE_THRESHOLDS.find(t => overall >= t.min)?.grade || 'F';

  // Calculate percentile (simplified - in production would compare against database)
  const percentile = Math.min(99, Math.max(1, Math.round(overall)));

  // Calculate confidence based on data completeness
  const confidence = calculateDataConfidence(input);

  return {
    overall,
    breakdown,
    tier,
    tierColor,
    percentile,
    grade,
    confidence,
  };
};

// ============ UTILITY FUNCTIONS ============

/**
 * Gets the tier for a given overall score
 */
export const getTierForScore = (score: number): TalentTier => {
  for (const [tierName, threshold] of Object.entries(TIER_THRESHOLDS)) {
    if (score >= threshold.min) {
      return tierName as TalentTier;
    }
  }
  return 'Bronze';
};

/**
 * Gets the tier color class for a given tier
 */
export const getTierColor = (tier: TalentTier): string => {
  return TIER_THRESHOLDS[tier].color;
};

/**
 * Gets the tier badge color for display
 */
export const getTierBadgeColor = (tier: TalentTier): string => {
  const colors: Record<TalentTier, string> = {
    Elite: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    Platinum: 'bg-slate-400/20 text-slate-300 border-slate-400/30',
    Gold: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Silver: 'bg-gray-400/20 text-gray-300 border-gray-400/30',
    Bronze: 'bg-orange-600/20 text-orange-400 border-orange-600/30',
  };
  return colors[tier];
};

/**
 * Formats rating for display
 */
export const formatRating = (rating: number): string => {
  return rating.toFixed(0);
};

/**
 * Gets rating color based on score
 */
export const getRatingColor = (score: number): string => {
  if (score >= 90) return 'text-violet-400';
  if (score >= 80) return 'text-slate-300';
  if (score >= 65) return 'text-yellow-400';
  if (score >= 50) return 'text-gray-300';
  return 'text-orange-400';
};

/**
 * Calculates rating from API athlete data
 */
export const calculateRatingFromAPIAthlete = (athlete: {
  sport: string;
  position?: string | null;
  current_team_name?: string | null;
  career_stats?: Record<string, any> | null;
  current_season_stats?: Record<string, any> | null;
  is_verified?: boolean | null;
  claim_status?: string | null;
  photo_url?: string | null;
  social_media?: Record<string, any> | null;
  market_value_usd?: number | null;
}): TalentRating => {
  // Extract follower count from social media if available
  let followers = 0;
  if (athlete.social_media) {
    const social = athlete.social_media as Record<string, any>;
    followers = (social.instagram_followers || 0) + 
                (social.twitter_followers || 0) + 
                (social.tiktok_followers || 0);
  }

  const input: TalentRatingInput = {
    sport: athlete.sport,
    position: athlete.position || undefined,
    teamName: athlete.current_team_name || undefined,
    careerStats: athlete.career_stats as Record<string, number> || undefined,
    currentSeasonStats: athlete.current_season_stats as Record<string, number> || undefined,
    isVerified: athlete.is_verified || false,
    isClaimed: athlete.claim_status === 'claimed',
    hasPhoto: !!athlete.photo_url,
    followers: followers > 0 ? followers : undefined,
    marketValue: athlete.market_value_usd || undefined,
  };

  return calculateTalentRating(input);
};

/**
 * Calculates rating from talent profile data
 */
export const calculateRatingFromProfile = (profile: {
  sport?: string | null;
  position?: string | null;
  team?: string | null;
  total_followers?: number | null;
  engagement_rate?: number | null;
  total_deals?: number | null;
  total_revenue?: number | null;
  is_verified?: boolean | null;
  avatar_url?: string | null;
  bio?: string | null;
  achievements?: string[] | null;
}): TalentRating => {
  // Calculate profile completeness
  let completeness = 0;
  if (profile.avatar_url) completeness += 15;
  if (profile.bio) completeness += 15;
  if (profile.sport) completeness += 10;
  if (profile.team) completeness += 10;
  if (profile.position) completeness += 10;
  if (profile.total_followers) completeness += 15;
  if (profile.achievements && profile.achievements.length > 0) completeness += 25;

  const input: TalentRatingInput = {
    sport: profile.sport || 'football',
    position: profile.position || undefined,
    teamName: profile.team || undefined,
    followers: profile.total_followers || undefined,
    engagementRate: profile.engagement_rate || undefined,
    completedDeals: profile.total_deals || undefined,
    totalRevenue: profile.total_revenue || undefined,
    isVerified: profile.is_verified || false,
    isClaimed: true,
    hasPhoto: !!profile.avatar_url,
    profileCompleteness: completeness,
  };

  return calculateTalentRating(input);
};
